/** Co-branded logo (PNG) — light mode. Used by UI chrome (nav, login, etc.). */
export const BRAND_LOGO_URL = '/uhm-ameritrust-logo-lightmode.png'

/** Dedicated dark-mode logo — white co-branded mark. */
export const BRAND_LOGO_DARK_URL = '/uhm-ameritrust-logo-darkmode.png'

export const BRAND_LOGO_ALT = 'Union Home Mortgage | AmeriTrust Mortgage'

/**
 * Document branding cutover (calendar date in PDT):
 * loans with opened_date before this day keep classic Ameritrust branding;
 * loans on/after this day use UHM + AmeriTrust co-brand.
 */
export const DOCUMENT_BRAND_CUTOVER_DATE = '2026-07-17'

export type DocumentBrandId = 'ameritrust' | 'uhm'

export interface DocumentBrand {
  id: DocumentBrandId
  /** Public light-mode logo path (preview / email attachments). */
  logoUrl: string
  /** Public dark-mode logo path (UI preview only). */
  logoDarkUrl: string
  logoAlt: string
  /** Short lender / program name (e.g. Anti-Steering Lender Name). */
  companyName: string
  /** Footer / legal line on generated PDFs. */
  companyLegalName: string
  teamName: string
}

const AMERITRUST_DOCUMENT_BRAND: DocumentBrand = {
  id: 'ameritrust',
  logoUrl: '/ameritrusttpo.png',
  logoDarkUrl: '/AmeritrustLogo_white.png',
  logoAlt: 'Ameritrust Mortgage',
  companyName: 'Ameritrust Mortgage',
  companyLegalName: 'Ameritrust Mortgage Corporation',
  teamName: 'Ameritrust Mortgage Team',
}

const UHM_DOCUMENT_BRAND: DocumentBrand = {
  id: 'uhm',
  logoUrl: '/uhm-ameritrust-logo-lightmode.png',
  logoDarkUrl: '/uhm-ameritrust-logo-darkmode.png',
  logoAlt: 'Union Home Mortgage | AmeriTrust Mortgage',
  companyName: 'Union Home Mortgage | AmeriTrust Mortgage',
  companyLegalName: 'Ameritrust Mortgage Corporation, A UHM Company',
  teamName: 'Ameritrust Mortgage Team',
}

/** Parse YYYY-MM-DD (or ISO datetime) to a local calendar date at midnight. */
function parseOpenedDateOnly(openedDate: string): Date | null {
  const trimmed = openedDate.trim()
  if (!trimmed) return null

  const ymd = trimmed.slice(0, 10)
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null
  }
  return date
}

/**
 * Resolve generated-document branding from bank_loan_statistics.opened_date.
 * Missing/invalid dates default to classic Ameritrust (safer for pre-rebrand loans).
 */
export function resolveDocumentBrand(openedDate?: string | null): DocumentBrand {
  const parsed = openedDate ? parseOpenedDateOnly(openedDate) : null
  if (!parsed) return AMERITRUST_DOCUMENT_BRAND

  const [y, m, d] = DOCUMENT_BRAND_CUTOVER_DATE.split('-').map(Number)
  const cutover = new Date(y, m - 1, d)
  return parsed < cutover ? AMERITRUST_DOCUMENT_BRAND : UHM_DOCUMENT_BRAND
}

/** Resolve from bank_loan_statistics.opened_date (almost always present). */
export function resolveDocumentBrandFromLoan(
  loan?: { opened_date?: string | null } | null
): DocumentBrand {
  return resolveDocumentBrand(loan?.opened_date ?? null)
}

/** Post-cutover / no-loan defaults (scenario pricer, new quotes). */
export function getCurrentDocumentBrand(): DocumentBrand {
  return UHM_DOCUMENT_BRAND
}
